/**
 * Bidi-isolation components — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/06-hebrew-rtl.md § 3
 *
 * Hebrew flow + LTR digit sequences = bidi bugs unless the digits are isolated
 * with `<bdi dir="ltr">`. Plate "5937839" inside a Hebrew sentence may render
 * as "9387395" without isolation. Gett's engineering team called this their
 * #1 RTL bug.
 *
 * Always use these components — never render a license plate / phone / VIN /
 * English brand name inline in Hebrew without one of these wrappers.
 */

import type { ReactNode } from 'react';

/**
 * Israeli license plate. Accepts the bare digits or the formatted form;
 * normalises and re-formats with the standard Israeli hyphenation rule.
 *
 *   7-digit (pre-2017): "XX-XXX-XX"  → e.g. "12-345-67"
 *   8-digit (post-2017): "XXX-XX-XXX" → e.g. "123-45-678"
 *
 * Always renders LTR with tabular numbers so plates align in lists.
 */
export function Plate({
  children,
  format = 'auto',
}: {
  children: string;
  format?: 'auto' | 'legacy' | 'modern';
}): JSX.Element {
  const digits = String(children).replace(/\D/g, '');
  const fmt = format === 'auto' ? (digits.length === 8 ? 'modern' : 'legacy') : format;
  let formatted = digits;
  if (fmt === 'legacy' && digits.length === 7) {
    formatted = `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
  } else if (fmt === 'modern' && digits.length === 8) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  return (
    <bdi dir="ltr" className="tabular tracking-tight">
      {formatted}
    </bdi>
  );
}

/**
 * Phone number. Accepts E.164 or domestic; renders as domestic with dashes
 * by default, E.164 if explicitly requested. Israeli mobile prefixes
 * (05X) are recognised; landlines fall through unchanged.
 */
export function Phone({
  children,
  format = 'domestic',
}: {
  children: string;
  format?: 'domestic' | 'e164';
}): JSX.Element {
  const digits = String(children).replace(/\D/g, '');
  // Normalise to local form starting with leading 0
  let local = digits;
  if (digits.startsWith('972')) local = '0' + digits.slice(3);
  let formatted = local;
  if (local.length === 10) {
    // Mobile + landline both 10 digits in normal form
    formatted = `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  } else if (local.length === 9) {
    // 9-digit landlines (02/03/04/08/09 + 7 digits)
    formatted = `${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
  }
  if (format === 'e164' && local.length >= 9) {
    const prefix = local.startsWith('0') ? local.slice(1, 3) : local.slice(0, 2);
    const rest = local.startsWith('0') ? local.slice(3) : local.slice(2);
    formatted = `+972-${prefix}-${rest}`;
  }
  return (
    <bdi dir="ltr" className="tabular">
      {formatted}
    </bdi>
  );
}

/**
 * English brand name inside Hebrew flow. Wraps in <bdi dir="ltr"> so trailing
 * punctuation doesn't jump to the wrong side of the brand token.
 *
 * Use for car makes/models, English service-provider names, English app
 * names inside Hebrew copy.
 */
export function Brand({ children }: { children: ReactNode }): JSX.Element {
  return <bdi dir="ltr">{children}</bdi>;
}

/**
 * Generic Bidi-isolation wrapper. Use when none of Plate/Phone/Brand apply —
 * e.g. VINs, OTP codes, IBANs, English-only display strings interpolated
 * into Hebrew flow.
 */
export function LTR({ children }: { children: ReactNode }): JSX.Element {
  return (
    <bdi dir="ltr" className="tabular">
      {children}
    </bdi>
  );
}
